import { sendRequest } from "@infrastructure/api/api";
import ApiResponse from "@core/types/apiResponse";
import { ApiMessageResponse } from "@core/types/apiMessageResponse";
import {
    EnableFinanceDeductibleRequest,
    EnableFinanceDeductibleResult,
    FinanceDeductible,
    FinanceDeductibleDetail,
    FinanceDeductibleImpact,
    FinanceDeductibleResponse,
    ReorderFinanceDeductibleRequest,
    SaveFinanceDeductibleRequest,
} from "@features/admin/finance/deductible/types/financeDeductible";

const toQueryParams = (params: Record<string, any>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        result[key] = typeof value === "boolean" ? (value ? "1" : "0") : String(value);
    }
    return result;
};

const base = (nationId: number) => `nation/${nationId}/finance/deductible`;

export const fetchFinanceDeductibles = async (
    nationId: number,
    params: Record<string, any>
): Promise<ApiResponse<FinanceDeductibleResponse | FinanceDeductible[]>> =>
    sendRequest<never, FinanceDeductibleResponse | FinanceDeductible[]>(
        "GET",
        `${base(nationId)}/query?${new URLSearchParams(toQueryParams(params))}`
    );

export const fetchFinanceDeductible = async (
    nationId: number,
    deductibleId: number
): Promise<ApiResponse<FinanceDeductibleDetail>> =>
    sendRequest<never, FinanceDeductibleDetail>("GET", `${base(nationId)}/${deductibleId}`);

export const fetchFinanceDeductibleImpact = async (
    nationId: number,
    deductibleId: number
): Promise<ApiResponse<FinanceDeductibleImpact>> =>
    sendRequest<never, FinanceDeductibleImpact>("GET", `${base(nationId)}/${deductibleId}/impact`);

export const createFinanceDeductible = async (
    nationId: number,
    data: SaveFinanceDeductibleRequest
): Promise<ApiResponse<ApiMessageResponse>> =>
    sendRequest<SaveFinanceDeductibleRequest, ApiMessageResponse>("POST", `${base(nationId)}/create`, data);

export const updateFinanceDeductible = async (
    nationId: number,
    deductibleId: number,
    data: SaveFinanceDeductibleRequest
): Promise<ApiResponse<ApiMessageResponse>> =>
    sendRequest<SaveFinanceDeductibleRequest, ApiMessageResponse>("POST", `${base(nationId)}/${deductibleId}/update`, data);

export const disableFinanceDeductible = async (
    nationId: number,
    deductibleId: number
): Promise<ApiResponse<ApiMessageResponse>> =>
    sendRequest<never, ApiMessageResponse>("POST", `${base(nationId)}/${deductibleId}/disable`);

export const enableFinanceDeductible = async (
    nationId: number,
    deductibleId: number,
    data: EnableFinanceDeductibleRequest
): Promise<ApiResponse<EnableFinanceDeductibleResult>> =>
    sendRequest<EnableFinanceDeductibleRequest, EnableFinanceDeductibleResult>(
        "POST",
        `${base(nationId)}/${deductibleId}/enable`,
        data
    );

export const deleteFinanceDeductible = async (
    nationId: number,
    deductibleId: number
): Promise<ApiResponse<ApiMessageResponse>> =>
    sendRequest<never, ApiMessageResponse>("POST", `${base(nationId)}/${deductibleId}/delete`);

export const reorderFinanceDeductibles = async (
    nationId: number,
    data: ReorderFinanceDeductibleRequest
): Promise<ApiResponse<ApiMessageResponse>> =>
    sendRequest<ReorderFinanceDeductibleRequest, ApiMessageResponse>("POST", `${base(nationId)}/reorder`, data);
